import { useEffect, useId, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import { setTenant, setTheme, toggleLineageOverlay, type PrefsState } from "@app/slices/prefsSlice";
import { useLocale } from "@hooks/useLocale";
import { usePalette, type Palette } from "@hooks/usePalette";
import { THEMES, TENANTS, TENANT_BRANDS, type Theme } from "@oxbo/tokens";
import { LOCALES, LOCALE_META } from "@i18n/locales";

/**
 * Settings cog — anchored at the bottom of the rail (kit `.cog` / `.cog-pop`).
 * Theme (Dark / Light / Auto), Palette (Calm / Vivid / CB-safe — the kit's
 * data-palette axis), Language (autonyms, never flags), the tenant white-label
 * switch, and the data_admin-only Data-lineage toggle (off by default).
 */
export function SettingsMenu({ collapsed }: { collapsed: boolean }) {
  const dispatch = useAppDispatch();
  const { locale, t, changeLocale } = useLocale();
  const theme = useAppSelector((s) => s.prefs.theme);
  const tenant = useAppSelector((s) => s.prefs.tenant);
  const role = useAppSelector((s: { prefs: PrefsState }) => s.prefs.role);
  const lineageOn = useAppSelector((s) => s.prefs.lineageOverlay);
  const { palette, palettes, setPalette } = usePalette();

  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const themeLabelKey = (th: Theme) =>
    th === "dark" ? "topbar.theme.dark" : th === "light" ? "topbar.theme.light" : "topbar.theme.auto";

  const paletteLabel: Record<Palette, string> = {
    calm: locale === "en" ? "Calm" : "Calma",
    vivid: locale === "en" ? "Vivid" : locale === "es" ? "Vívida" : "Vívida",
    "cb-safe": locale === "en" ? "CB-safe" : "Daltónica",
  };

  return (
    <div className="cog" ref={rootRef}>
      <button
        type="button"
        className="btn ghost"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={t("settings.open")}
        title={t("settings.title")}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="ic ic-settings" aria-hidden="true" />
        {!collapsed && <span className="nav-label">{t("settings.title")}</span>}
      </button>

      {open && (
        <div className="cog-pop open" role="dialog" id={panelId} aria-label={t("settings.title")}>
          <h4>{t("topbar.theme")}</h4>
          <div className="seg" role="group" aria-label={t("topbar.theme")}>
            {THEMES.map((th) => (
              <button
                key={th}
                type="button"
                className={theme === th ? "on" : ""}
                aria-pressed={theme === th}
                onClick={() => dispatch(setTheme(th))}
              >
                {t(themeLabelKey(th))}
              </button>
            ))}
          </div>

          <h4>{locale === "en" ? "Palette" : "Paleta"}</h4>
          <div className="seg" role="group" aria-label={locale === "en" ? "Palette" : "Paleta"}>
            {palettes.map((p) => (
              <button
                key={p}
                type="button"
                className={palette === p ? "on" : ""}
                aria-pressed={palette === p}
                onClick={() => setPalette(p)}
              >
                {paletteLabel[p]}
              </button>
            ))}
          </div>

          <h4>{t("topbar.locale")}</h4>
          <div className="seg" role="group" aria-label={t("topbar.locale")}>
            {LOCALES.map((lc) => (
              <button
                key={lc}
                type="button"
                className={locale === lc ? "on" : ""}
                aria-pressed={locale === lc}
                onClick={() => changeLocale(lc)}
              >
                {LOCALE_META[lc].label}
              </button>
            ))}
          </div>

          <h4>{t("settings.tenant")}</h4>
          <div className="seg" role="group" aria-label={t("settings.tenant")}>
            {TENANTS.map((tn) => (
              <button
                key={tn}
                type="button"
                className={tenant === tn ? "on" : ""}
                aria-pressed={tenant === tn}
                onClick={() => dispatch(setTenant(tn))}
              >
                {TENANT_BRANDS[tn].wordmark}
              </button>
            ))}
          </div>

          {role === "data_admin" && (
            <>
              <h4>
                {t("topbar.lineage")} <span className="dim">({t("topbar.lineage.adminOnly")})</span>
              </h4>
              <div className="rowflex">
                <span>{lineageOn ? t("topbar.lineage.on") : t("topbar.lineage.off")}</span>
                <span
                  className={`switch${lineageOn ? " on" : ""}`}
                  role="switch"
                  aria-checked={lineageOn}
                  aria-label={t("topbar.lineage")}
                  tabIndex={0}
                  onClick={() => dispatch(toggleLineageOverlay())}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      dispatch(toggleLineageOverlay());
                    }
                  }}
                />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
